import every from 'lodash/every';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import baseState from './baseState';
import webApiClientSingleton from '../webApiClientSingleton';
import tuner from '../tuner';
import streamMapper from '../streamMapper';
import nowPlayingPoller from '../nowPlayingPoller';
import getAdswizzListenerId from '../utils/getAdswizzListenerId';
import cancelable from '../utils/cancelable';
import { sendTimingEvent } from '../loggers/timing';

const htmlStreamFormat = 'html';
const programPrefix = 'p';
let guideId;
let cancelableTunePromise;
let loadingPromise;

function onFail(error) {
  loadingPromise.reject(error);
  tuner.changeState(tuner.states.failed, error);
}

function filterStreams(streams, formats) {
  if (formats) {
    return filter(streams, function (stream) {
      return includes(formats, stream.format);
    });
  }

  return streams;
}

function handleStreams(streams, singleTestStream) {
  const loadingMetadata = {};

  if (singleTestStream) {
    tuner.streams = streams;
    tuner.changeState(tuner.states.loaded);
    return;
  }

  const mappedStreams = streamMapper.fromTuneResponse(
    streams,
    tuner.forceHttpsStreams,
  );
  const filteredStreams = filterStreams(mappedStreams, tuner.tune.formats);

  if (!filteredStreams.length) {
    const error = new Error('no formats');
    error.errorData = { guideId };
    onFail(error);

    // don't resolve loading promise on fail
    return;
  }

  if (tuner.tune.guideId === tuner.boostState.guideId) {
    loadingPromise.resolve(loadingMetadata);
    tuner.changeState(tuner.states.loaded);
    return;
  }

  if (every(filteredStreams, (stream) => stream.format === htmlStreamFormat)) {
    // first stream is used because there is currently
    // no support for selection of multiple HTML streams.
    // better approach would be to trigger this event with all streams
    // and let the callee decide what to do with them.
    tuner.trigger(tuner.events.htmlStreamLoaded, filteredStreams[0].url);

    loadingMetadata.isHtmlStream = true;
  } else {
    // NOTE - exclude the following:
    // 1. html streams as they cannot be played by our streamPlayer
    // 2. streams with isBoostStation: true, as they are handled separately & should not be used as a fallback
    tuner.streams = filter(
      filteredStreams,
      (stream) => stream.format !== htmlStreamFormat && !stream.isBoostStation,
    );
    tuner.boostState.stream = filteredStreams.find(
      (stream) => !!stream.isBoostStation,
    );
    // Retain a reference to the content guide id, as we need to use it for:
    // Tune API: responsible for backend reporting and updates user's Recents history
    // Now Playing API: our UI is dependent on the specific NP payload of the content guide id
    tuner.boostState.parentGuideId = tuner.boostState.stream
      ? tuner.tune.guideId
      : null;

    tuner.changeState(tuner.states.loaded);
  }

  const { rejectReason } = get(tuner, 'streams[0]', {});

  if (
    tuner.blockRejectedStreamsPlaybackMap &&
    tuner.blockRejectedStreamsPlaybackMap[rejectReason]
  ) {
    loadingMetadata.rejectReason = rejectReason;
    nowPlayingPoller.setRejectReasonKey(
      tuner.blockRejectedStreamsPlaybackMap[rejectReason],
    );
    tuner.changeState(tuner.states.stopped);
  }

  loadingPromise.resolve(loadingMetadata);
}

function onTuneSuccess(tuneResponse) {
  const { head } = tuneResponse.data;

  if (head.status !== '200') {
    const error = new Error('tune.ashx response error');
    error.errorData = tuneResponse;
    onFail(error);
    return;
  }

  const streams = tuneResponse.data.body;

  if (isEmpty(streams)) {
    const error = new Error('no tunable streams');
    error.errorData = { guideId };
    onFail(error);
    return;
  }

  handleStreams(streams, false);
}

function onNowPlayingChanged(data) {
  if (data) {
    tuner.trigger(tuner.events.nowPlayingChanged, data);
  }
}

function tune(promise) {
  const {
    itemToken,
    formats,
    type,
    testStreamId,
    testStreamUrl,
    testStreamFormat,
    guideId: tuneGuideId,
    isUserSubscribed,
    params = {},
  } = get(tuner, 'tune', {});
  guideId = testStreamId || tuneGuideId;
  loadingPromise = promise;
  const cachedStreamData = tuner.preloadCache.get(guideId);

  if (cachedStreamData) {
    tuner.streams = cachedStreamData.streams;
    onNowPlayingChanged(cachedStreamData.nowPlaying);
    loadingPromise.resolve({});
    tuner.changeState(tuner.states.loaded);
    return;
  }

  if (testStreamUrl) {
    /*
      the handleStreams block is normally executed on a
      successful tune call. This bypasses that with a
      test stream url.
    */
    handleStreams(
      [
        {
          url: testStreamUrl,
          requiresUnwrap: false,
          isFinalStream: true,
          format: testStreamFormat || 'mp3',
          position: 0,
        },
      ],
      {},
      true,
    ); // pass true to indicate that this is a single test stream
  } else {
    const { audience, usPrivacyString, palNonce, gdprTcString, gdprApplies } =
      params;
    const loadOptions = {
      id: guideId,
      listenId: tuner.listenId,
      itemToken,
      formats,
      type,
      us_privacy: usPrivacyString,
      audience,
    };

    if (palNonce) {
      loadOptions.paln = palNonce;
    }

    // gdprApplies is undefined until OneTrust determines whether GDPR applies
    // IMPORTANT: GDPR ATTRIBUTE MUST BE STRING
    if (isBoolean(gdprApplies)) {
      loadOptions.gdpr = gdprApplies ? '1' : '0';
    }

    if (gdprTcString) {
      loadOptions.gdpr_consent = gdprTcString;
    }

    const adswizzListenerId = getAdswizzListenerId();
    if (adswizzListenerId) {
      loadOptions.adswizzListenerId = adswizzListenerId;
    }

    const tuneTimeingStart = performance.now();
    const tunePromise = webApiClientSingleton.instance.tune
      .load(loadOptions)
      .then((result) => {
        sendTimingEvent('tune', tuneTimeingStart);
        return result;
      });
    cancelableTunePromise = cancelable(tunePromise);

    const nowPlayingAndTune = function (nowPlayingGuideId) {
      const allPromises = [cancelableTunePromise];
      const nowPlayingTimingStart = performance.now();
      const nowPlayingPromise = nowPlayingPoller
        .fetchOnce(
          tuner.boostState.parentGuideId || nowPlayingGuideId,
          onNowPlayingChanged,
        )
        .then((result) => {
          sendTimingEvent('nowPlaying', nowPlayingTimingStart);
          return result;
        });

      if (!isUserSubscribed) {
        // the Now Playing response has ad-eligibility metadata that we need for free users,
        // so we'll wait for that to resolve, too, so that we have it ready for preroll requests.
        allPromises.push(nowPlayingPromise);
      }

      const tuneAndNowPlayingTimingStart = performance.now();
      Promise.all(allPromises)
        .then(([tuneResponse]) => {
          sendTimingEvent('tuneAndNowPlaying', tuneAndNowPlayingTimingStart);
          return onTuneSuccess(tuneResponse);
        })
        .catch(onFail);
    };

    if (guideId[0] === programPrefix) {
      const profilesPromise =
        webApiClientSingleton.instance.profile.getV3(guideId);
      profilesPromise.then((profiles) => {
        // if it's a program, get the nowPlaying from the profiles call for the playing station
        const profileGuideId = get(
          profiles,
          'data.items[0].children[0].guideId',
          guideId,
        );
        nowPlayingAndTune(profileGuideId);
      });
    } else {
      nowPlayingAndTune(guideId);
    }
  }
}

function cancelCurrentPromises() {
  if (cancelableTunePromise) cancelableTunePromise.cancel();
  if (loadingPromise) loadingPromise.promise.cancel();
}

const state = {
  name: 'loading',
  enter: function (previousState, context) {
    tuner.trigger(tuner.events.loading);
    tune(context);
  },
  exit: function () {
    cancelCurrentPromises();
  },
  load: function (promise) {
    cancelCurrentPromises();
    tune(promise);
  },
};

export default {
  ...baseState,
  ...state,
};
